<template>
  <div>
    <np-action-btn
      v-if="!creatingSharedMap"
      :disabled="disabled"
      class="float-right"
      @click="handleCreateClick()"
    >
      {{ btnText }}
    </np-action-btn>
    <div
      v-else
      class="d-flex align-center float-right"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        :size="20"
        class="mr-2 mb-1"
      />
      <span class="font-weight-bold">{{ loadingText }}</span>
    </div>
  </div>
</template>

<script>
// 3rd party
import {mapActions} from 'vuex';

// Helpers
import {MapService, gmAPI} from '@/services/MapService';
import {exportFormatFunctions} from '@/lib/classes/InfoWindowContent/helpers/shared';
import {
  formatFeatures,
  formatMarkerFeatures,
  formatOverlayFeatures,
  saveExportFieldSelections,
} from '@/lib/classes/SharedMap';
import {createHttpClient} from '@/services/HttpService';

/** @typedef {import('@/lib/classes/SharedMap/exportFields').ExportFieldSettings} ExportFieldSettings */

export default {
  name: 'CreateSharedMapButton',
  props: {
    selectedMap: {
      type: Object,
      default: null,
    },
    /** Hydrated items per collection name, gathered by the dialog; null while gathering. */
    exportItems: {
      type: Map,
      default: null,
    },
    newMapName: {
      type: String,
      default: 'Untitled Map',
    },
    btnText: {
      type: String,
      default: 'Create Shared Map',
    },
    loadingText: {
      type: String,
      default: 'Creating Shared Map...',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /** @type {ExportFieldSettings}} */
    exportFieldSettings: {
      type: Map,
      default: () => new Map(),
    },
  },
  emits: ['close'],
  data() {
    return {
      creatingSharedMap: false,
      maxItems: 100,
    };
  },
  methods: {
    ...mapActions(['addMessage']),
    async combineExportItems() {
      const allFeatures = [];
      const featureFolders = [];
      const overlayFolders = [];
      const markerFolders = [];

      for (const [collectionName, items] of this.exportItems) {
        switch (collectionName) {
          case 'overlays': {
            const [features, folders] = await formatOverlayFeatures(items, this.exportFieldSettings);
            allFeatures.push(...features);
            overlayFolders.push(...folders);
            break;
          }
          case 'features': {
            const [features, folders] = await formatFeatures(items, this.exportFieldSettings);
            allFeatures.push(...features);
            featureFolders.push(...folders);
            break;
          }
          default: {
            const formatFn = exportFormatFunctions.get(collectionName);
            const [features, folders] = await formatMarkerFeatures(
              items,
              collectionName,
              formatFn,
              this.exportFieldSettings,
            );
            allFeatures.push(...features);
            markerFolders.push(...folders);
          }
        }
      }

      // Calculate the bounds of the overlay using all features
      const maxBounds = new gmAPI.LatLngBounds();
      allFeatures.forEach((feature) => {
        maxBounds.extend({lat: feature.bounds.north, lng: feature.bounds.east});
        maxBounds.extend({lat: feature.bounds.south, lng: feature.bounds.west});
      });

      // Return the object that represents a shared map
      return {
        bounds: maxBounds,
        features: allFeatures,
        // Concatenate so that marker features are listed before overlay features
        folders: markerFolders.concat(overlayFolders).concat(featureFolders),
      };
    },
    async handleCreateClick() {
      this.creatingSharedMap = true;

      // Remember this selection so the next export's dialog prepopulates with the same field choices.
      saveExportFieldSelections(this.exportFieldSettings).catch((error) => {
        console.error('Failed to save export field selections:', error);
      });

      try {
        // Format shared map data for export
        const sharedMapData = await this.combineExportItems();
        const mapCenter = MapService.getMap().getCenter();
        sharedMapData.mapPosition = {
          lat: mapCenter.lat(),
          lng: mapCenter.lng(),
          zoom: MapService.getMap().getZoom(),
        };
        sharedMapData.mapName = this.newMapName;
        sharedMapData.existingMapId = this.selectedMap?.id ?? null;
        sharedMapData.fromProject = process.env.VUE_APP_GCP_PROJECT_ID;

        // Staging and prod instances of upsertSharedMap live in SMAPS projects, so if we export from BMAPS, we must
        // force the URL to the correct project
        const client = createHttpClient('SHARED_MAPS');
        const response = await client.post('upsertSharedMap', sharedMapData);

        if (response.error) {
          // User does not exist in Shareable Maps database
          if (response.error.includes('There is no user record corresponding to the provided identifier')) {
            const stagingEnvironment = process.env.VUE_APP_GCP_ENV !== 'production';
            const redirectUrl = `https://maps.northpointkc.com${stagingEnvironment ? '/beta' : ''}`;
            this.addMessage({
              header: 'Failed to create a shared map.',
              messageType: 'danger',
              body: `You must authenticate in Shareable Maps first.\nClick the below, then return to this page and retry.`,
              actions: [{title: 'To Shareable Maps', callback: () => window.open(redirectUrl, '_blank')?.focus(), class: 'primary'}],
            });
            this.creatingSharedMap = false;
            this.$emit('close');
            return;
          }
          // If features have failed to be copied over but the map was still created, a url will be provided.
          if (response.url) {
            this.addMessage({
              header: 'Map created with errors. Please check the map for issues.',
              messageType: 'danger',
              body: `The following features were unable to be copied over to the shared map: `,
              bulletedList: response.skippedFeatures ?? [],
            });
            // Open the shared map in a new tab
            window.open(response.url, '_blank')?.focus();
            this.creatingSharedMap = false;
            this.$emit('close');
            return;
          }
          throw new Error(`Could not upsert Shared Map: ${response.error}`);
        }
        if (!response.url) {
          throw new Error('No URL returned from the server.');
        }

        // Quick hack to change the url env to release if the user built the Shared Map in release BMAPS
        if (process.env.VUE_APP_VUE_CLI_MODE === 'release') {
          response.url = response.url.replace('/beta/', '/release/');
        }

        // Quick hack to redirect to local external app when developing locally
        if (window.location.origin === 'http://localhost:8080') {
          const path = new URL(response.url).pathname + new URL(response.url).search + new URL(response.url).hash;
          response.url = `http://localhost:9090${path}`;
        }

        // Open the shared map in a new tab
        window.open(response.url, '_blank')?.focus();
      } catch (error) {
        this.$emit('close');
        error.message = `Error creating shared map: ${error.message}`;
        console.error(error);
        this.addMessage({
          header: 'Failed to create a shared map.',
          messageType: 'danger',
          body: 'Please contact solutions-engineering@northpointkc.com',
        });
      }
      this.creatingSharedMap = false;
      this.$emit('close');
    },
  },
};
</script>
